import { useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { getInspirationDetail, getTaskDetail, getWorkDetail } from '@/app/apis';
import { toast } from '@/app/components/ui/use-toast';
import { RECREATE_INSPIRATION_KEY, RECREATE_INSPIRATION_SOURCE_KEY, RECREATE_TASK_KEY } from '@/app/constants';
import type { TaskInfo } from '@/app/types';
import { isImageToVideoTaskType } from '@/app/utils';
import { useFetch } from './use-fetch';
import { useProcessRecreateData } from './use-process-recreate-data';

const cleanupURLParams = (paramsToRemove: string[]) => {
  const searchParams = new URLSearchParams(window.location.search);
  paramsToRemove.forEach((param) => {
    searchParams.delete(param);
  });
  const newUrl = `${window.location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}${window.location.hash}`;
  window.history.replaceState({}, '', newUrl);
};

const RECREATE_QUERY_KEYS = [RECREATE_INSPIRATION_KEY, RECREATE_INSPIRATION_SOURCE_KEY, RECREATE_TASK_KEY];

interface UseFetchRecreateDataOptions {
  onTaskSuccess?: (task: TaskInfo, inspirationId?: string, source?: string) => void;
}

/**
 * 通过灵感id，获取灵感详情，并进行数据处理，适用于灵感
 * 通过taskid，获取task详情，并进行数据处理，适用于非灵感
 * 适用于新窗口打开【做同款】场景
 */
export const useFetchRecreateData = ({ onTaskSuccess }: UseFetchRecreateDataOptions = {}) => {
  const searchParams = useSearchParams();
  const { processRecreateData } = useProcessRecreateData();
  const paramsToDeleteRef = useRef<string[]>([]);

  // 清理URL参数
  const clearURLParams = () => {
    if (paramsToDeleteRef.current.length === 0) return;

    cleanupURLParams(paramsToDeleteRef.current);
    paramsToDeleteRef.current = [];
  };

  const handleTaskInfo = (task?: TaskInfo, inspirationId?: string, source?: string) => {
    const taskType = task?.type || 'unspecified';
    const tab = isImageToVideoTaskType(taskType) ? 'img2video' : taskType;

    processRecreateData({
      creationId: task?.creations[0]?.id || '',
      settings: task?.settings || {},
      input: task?.input || { enhance: true },
      templateScene: task?.scene,
      tab: tab,
      inspirationId: inspirationId,
      source: source,
      taskType: taskType,
    });
  };

  const { run: fetchInspirationDetail } = useFetch(
    (id: string, from?: string) =>
      (from === 'work' ? getWorkDetail : getInspirationDetail)({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }, params) => {
        if (error) {
          toast({
            type: 'error',
            message: error.message,
          });
        }
        if (data) {
          if (onTaskSuccess && data.task) {
            onTaskSuccess(data.task, params[0], params[1]);
            return;
          }
          handleTaskInfo(data.task, params[0], params[1]);
        }
      },
      onFinally: () => {
        clearURLParams();
      },
    },
  );

  const { run: fetchTaskDetail } = useFetch(
    (id: string) =>
      getTaskDetail({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (error) {
          toast({
            type: 'error',
            message: error.message,
          });
        }
        if (data) {
          if (onTaskSuccess) {
            onTaskSuccess(data);
            return;
          }
          handleTaskInfo(data);
        }
      },
      onFinally: () => {
        clearURLParams();
      },
    },
  );

  // 从 url 中获取灵感 id / taskid ，并删除 url 中的灵感 id / taskid
  useEffect(() => {
    const inspirationId = searchParams.get(RECREATE_INSPIRATION_KEY) || '';
    const inspirationFrom = searchParams.get(RECREATE_INSPIRATION_SOURCE_KEY) || '';
    const taskId = searchParams.get(RECREATE_TASK_KEY) || '';

    // 根据参数调用相应的获取函数
    if (inspirationId) {
      paramsToDeleteRef.current = RECREATE_QUERY_KEYS;
      fetchInspirationDetail(inspirationId, inspirationFrom);
    } else if (taskId) {
      paramsToDeleteRef.current = RECREATE_QUERY_KEYS;
      fetchTaskDetail(taskId);
    }
  }, [fetchInspirationDetail, fetchTaskDetail, searchParams]);
};
